import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DestinationPicker, type DestinationValue } from '../components/DestinationPicker';
import { FormFooter, PageHeader, Row, Section, Select, Tabs, Toggle } from '../components/form';
import { PromptPicker } from '../components/PromptPicker';
import { Alert, Badge, Input, cn } from '../components/ui';
import { ApiError, api } from '../lib/api';

/** 0–9 then star and hash, which is the order they sit on a keypad. */
const KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'] as const;

const NONE: DestinationValue = {
  destinationType: 'hangup',
  destinationId: null,
  destinationValue: null,
};

interface IvrDetail {
  id: string;
  name: string;
  number: string | null;
  greetingMediaId: string | null;
  invalidMediaId: string | null;
  timeoutMediaId: string | null;
  digitTimeoutMs: number;
  responseTimeoutMs: number;
  maxRetries: number;
  allowDirectDial: boolean;
  timeoutDestinationType: string;
  timeoutDestinationId: string | null;
  timeoutDestinationValue: string | null;
  invalidDestinationType: string;
  invalidDestinationId: string | null;
  invalidDestinationValue: string | null;
  options: Array<{
    digit: string;
    label: string | null;
    destinationType: string;
    destinationId: string | null;
    destinationValue: string | null;
  }>;
}

/**
 * The IVR editor.
 *
 * Two tabs, matching what PBX admins expect: the menu's own settings, then one row per key. The
 * key list is always all twelve rather than a list you add rows to, because the question an admin
 * is answering is "what happens when somebody presses 4", and a key with no destination is a
 * meaningful answer — it is not the same as the key being absent.
 */
export function IvrEditor({ id, onClose }: { id: string | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('basic');
  const [loaded, setLoaded] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>('0');

  const existing = useQuery({
    queryKey: ['ivr', id],
    queryFn: () => api.get<IvrDetail>(`/api/v1/ivrs/${id}`),
    enabled: id !== null,
  });

  const [form, setForm] = useState({
    name: '',
    number: '',
    greetingMediaId: null as string | null,
    invalidMediaId: null as string | null,
    timeoutMediaId: null as string | null,
    responseTimeoutMs: 10000,
    digitTimeoutMs: 4000,
    maxRetries: 3,
    allowDirectDial: false,
  });

  const [timeout, setTimeoutDest] = useState<DestinationValue>(NONE);
  const [invalid, setInvalidDest] = useState<DestinationValue>(NONE);
  const [keys, setKeys] = useState<Record<string, DestinationValue | null>>({});

  if (id !== null && existing.data && !loaded) {
    const ivr = existing.data;
    setForm({
      name: ivr.name,
      number: ivr.number ?? '',
      greetingMediaId: ivr.greetingMediaId,
      invalidMediaId: ivr.invalidMediaId,
      timeoutMediaId: ivr.timeoutMediaId,
      responseTimeoutMs: ivr.responseTimeoutMs,
      digitTimeoutMs: ivr.digitTimeoutMs,
      maxRetries: ivr.maxRetries,
      allowDirectDial: ivr.allowDirectDial,
    });
    setTimeoutDest({
      destinationType: ivr.timeoutDestinationType,
      destinationId: ivr.timeoutDestinationId,
      destinationValue: ivr.timeoutDestinationValue,
    });
    setInvalidDest({
      destinationType: ivr.invalidDestinationType,
      destinationId: ivr.invalidDestinationId,
      destinationValue: ivr.invalidDestinationValue,
    });
    setKeys(
      Object.fromEntries(
        ivr.options.map((o) => [
          o.digit,
          {
            destinationType: o.destinationType,
            destinationId: o.destinationId,
            destinationValue: o.destinationValue,
          },
        ]),
      ),
    );
    setLoaded(true);
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        ...(form.number ? { number: form.number } : {}),
        ...(form.greetingMediaId ? { greetingMediaId: form.greetingMediaId } : {}),
        ...(form.invalidMediaId ? { invalidMediaId: form.invalidMediaId } : {}),
        ...(form.timeoutMediaId ? { timeoutMediaId: form.timeoutMediaId } : {}),
        responseTimeoutMs: form.responseTimeoutMs,
        digitTimeoutMs: form.digitTimeoutMs,
        maxRetries: form.maxRetries,
        allowDirectDial: form.allowDirectDial,
        timeout,
        invalid,
        // Only keys with a destination are sent; an untouched key is simply absent, which is what
        // "nothing happens when you press it" means.
        options: Object.entries(keys)
          .filter(([, dest]) => dest !== null)
          .map(([digit, dest]) => ({ digit, ...(dest as DestinationValue) })),
      };

      return id === null ? api.post('/api/v1/ivrs', body) : api.patch(`/api/v1/ivrs/${id}`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ivrs'] });
      void queryClient.invalidateQueries({ queryKey: ['destinations'] });
      onClose();
    },
  });

  const conflict = save.error instanceof ApiError && save.error.isConflict;
  const assigned = KEYS.filter((k) => keys[k]).length;

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <PageHeader
        title={id === null ? 'New IVR menu' : `Edit ${form.name || 'IVR'}`}
        description="What a caller hears, and where each key sends them."
      />

      {save.error && (
        <Alert>{conflict ? 'That number is already in use.' : (save.error as Error).message}</Alert>
      )}

      <Tabs
        tabs={[
          { id: 'basic', label: 'Basic' },
          { id: 'keys', label: `Key press event${assigned > 0 ? ` (${assigned})` : ''}` },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'basic' ? (
        <>
          <Section title="Menu">
            <div className="grid gap-4 sm:grid-cols-2">
              <Row label="Name" required>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Main menu"
                  required
                />
              </Row>

              <Row label="Number" hint="Optional, so staff can dial the menu internally.">
                <Input
                  value={form.number}
                  onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))}
                  placeholder="6201"
                />
              </Row>

              <PromptPicker
                label="Greeting"
                required
                hint="Played when the caller arrives. Upload with the button on the right."
                value={form.greetingMediaId}
                onChange={(v) => setForm((f) => ({ ...f, greetingMediaId: v }))}
              />

              <Row
                label="Retries"
                required
                hint="How many times the greeting repeats before giving up."
              >
                <Select
                  value={String(form.maxRetries)}
                  onChange={(v) => setForm((f) => ({ ...f, maxRetries: Number(v) }))}
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </Row>

              <Row label="Response timeout" required hint="Seconds to wait for any key at all.">
                <Select
                  value={String(form.responseTimeoutMs / 1000)}
                  onChange={(v) => setForm((f) => ({ ...f, responseTimeoutMs: Number(v) * 1000 }))}
                >
                  {[3, 5, 10, 15, 20, 30].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </Row>

              <Row
                label="Digit timeout"
                required
                hint="Seconds between digits of a multi-digit entry."
              >
                <Select
                  value={String(form.digitTimeoutMs / 1000)}
                  onChange={(v) => setForm((f) => ({ ...f, digitTimeoutMs: Number(v) * 1000 }))}
                >
                  {[1, 2, 3, 4, 5, 8].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </Row>
            </div>

            <div className="mt-5 flex items-start gap-3">
              <Toggle
                checked={form.allowDirectDial}
                onChange={(v) => setForm((f) => ({ ...f, allowDirectDial: v }))}
                label="Allow callers to dial an extension"
              />
              <div>
                <div className="text-sm font-medium">Allow callers to dial an extension</div>
                <div className="text-xs text-muted-foreground">
                  Any extension number, not only the keys below. Menu keys that are also the first
                  digit of an extension wait the full digit timeout before deciding.
                </div>
              </div>
            </div>
          </Section>

          <Section
            title="When nothing works"
            description="Where a caller goes after too many retries, or after pressing a key with nothing on it."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <PromptPicker
                label="Timeout prompt"
                hint="Optional. Played before giving up."
                value={form.timeoutMediaId}
                onChange={(v) => setForm((f) => ({ ...f, timeoutMediaId: v }))}
              />
              <PromptPicker
                label="Invalid prompt"
                hint="Optional. Played after an unassigned key."
                value={form.invalidMediaId}
                onChange={(v) => setForm((f) => ({ ...f, invalidMediaId: v }))}
              />
              <DestinationPicker
                label="After timeout, go to"
                value={timeout}
                onChange={setTimeoutDest}
                {...(id ? { excludeId: id } : {})}
              />
              <DestinationPicker
                label="After invalid entry, go to"
                value={invalid}
                onChange={setInvalidDest}
                {...(id ? { excludeId: id } : {})}
              />
            </div>
          </Section>
        </>
      ) : (
        <Section
          title="Key destination"
          description="Every key on the pad. A key with no destination does nothing when pressed."
        >
          <div className="space-y-2">
            {KEYS.map((key) => {
              const dest = keys[key] ?? null;
              const open = openKey === key;

              return (
                <div key={key} className="rounded-[--radius] border border-border">
                  <button
                    type="button"
                    onClick={() => setOpenKey(open ? null : key)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40"
                  >
                    {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    <span className="font-medium">Press {key}</span>
                    {dest ? (
                      <Badge tone="success">{dest.destinationType.replace('_', ' ')}</Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">Nothing</span>
                    )}
                  </button>

                  {open && (
                    <div className="border-t border-border p-4">
                      <div className="max-w-md">
                        <DestinationPicker
                          label="Destination"
                          value={dest ?? NONE}
                          onChange={(next) => setKeys((k) => ({ ...k, [key]: next }))}
                          {...(id ? { excludeId: id } : {})}
                        />
                      </div>

                      {dest && (
                        <button
                          type="button"
                          onClick={() => setKeys((k) => ({ ...k, [key]: null }))}
                          className={cn(
                            'mt-3 text-xs text-muted-foreground underline underline-offset-2',
                            'hover:text-destructive',
                          )}
                        >
                          Clear this key
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <FormFooter onCancel={onClose} saving={save.isPending} disabled={!form.name} />
    </form>
  );
}
